// Workspace → Brand logo uploader. Three steps per file, matching the
// profile avatar:
//   1. POST /workspace/{id}/logo/sign   → presigned PUT URL
//   2. PUT bytes directly to the presigned URL
//   3. POST /workspace/{id}/logo/commit → the stored render URL
// "Remove" hits DELETE /workspace/{id}/logo.
//
// The workspace comes from the widget rather than the session: the
// Brand tab is addressed by URL, and an owner with two workspaces open
// can switch the active one in either tab.
//
// The card preview beside the color pickers is server-rendered from
// the stored key, so both paths finish by poking the form the preview
// listens to rather than patching the card here.

import { onceGuard } from "./bind_once.js";
import { postForm, request, signAndPut } from "./presigned_upload.js";

const firstBind = onceGuard();

const ROOT_SELECTOR = "[data-workspace-logo]";
const FALLBACK_UPLOAD_ERROR = "Could not upload your logo. Please try again.";
const FALLBACK_REMOVE_ERROR = "Could not remove your logo. Please try again.";

export function initWorkspaceLogo(root = document) {
  root.querySelectorAll(ROOT_SELECTOR).forEach(wireWidget);
}

function wireWidget(widget) {
  if (!firstBind(widget)) return;

  const base = `/workspace/${widget.dataset.workspaceLogo}/logo`;
  const input = widget.querySelector("[data-workspace-logo-input]");
  const removeBtn = widget.querySelector("[data-workspace-logo-remove]");
  const errorEl = widget.querySelector("[data-workspace-logo-error]");
  const statusEl = widget.querySelector("[data-workspace-logo-status]");

  if (input) {
    input.addEventListener("change", async () => {
      const file = input.files?.[0];
      if (!file) return;

      hide(errorEl);
      setStatus(statusEl, "Uploading…");
      input.disabled = true;

      try {
        const logoURL = await uploadOne(base, file);
        showLogo(widget, logoURL);
        refreshPreview();
      } catch (err) {
        console.error("workspace logo upload failed", err);
        showError(errorEl, err.userMessage || FALLBACK_UPLOAD_ERROR);
      } finally {
        hide(statusEl);
        input.disabled = false;
        input.value = "";
      }
    });
  }

  if (removeBtn) {
    removeBtn.addEventListener("click", async () => {
      hide(errorEl);
      removeBtn.disabled = true;
      setStatus(statusEl, "Removing…");

      try {
        await request(base, { method: "DELETE" });

        showLogo(widget, "");
        refreshPreview();
      } catch (err) {
        console.error("workspace logo removal failed", err);
        showError(errorEl, err.userMessage || FALLBACK_REMOVE_ERROR);
      } finally {
        removeBtn.disabled = false;
        hide(statusEl);
      }
    });
  }
}

async function uploadOne(base, file) {
  const signed = await signAndPut(`${base}/sign`, file);

  const committed = await postForm(`${base}/commit`, { key: signed.key });

  return committed.logo_url || "";
}

// showLogo swaps the widget's own tile between the uploaded image and
// the lettered fallback, and moves the two buttons with it.
function showLogo(widget, logoURL) {
  const tile = widget.querySelector("[data-workspace-logo-tile]");
  const label = widget.querySelector("[data-workspace-logo-label]");
  const removeBtn = widget.querySelector("[data-workspace-logo-remove]");

  if (tile) {
    if (logoURL) {
      const img = tile.querySelector("img") || document.createElement("img");
      img.src = logoURL;
      img.alt = "";
      img.className = "h-full w-full object-cover";
      tile.replaceChildren(img);
    } else {
      // The initial rides on the tile as data, because the uploaded
      // image replaced it in the DOM and removing has to put it back.
      tile.replaceChildren(tile.dataset.workspaceLogoTile || "");
    }
  }

  if (label) label.textContent = logoURL ? "Replace" : "Upload logo";

  removeBtn?.classList.toggle("hidden", !logoURL);
}

// refreshPreview re-runs the card preview's own render. It listens for
// an input event on the brand form, which is also what a color change
// fires, so the logo takes the same path rather than a second one.
function refreshPreview() {
  document
    .getElementById("brand-form")
    ?.dispatchEvent(new Event("input", { bubbles: true }));
}

function showError(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}

function hide(el) {
  if (!el) return;
  el.textContent = "";
  el.classList.add("hidden");
}

function setStatus(el, message) {
  if (!el) return;
  el.textContent = message;
  el.classList.remove("hidden");
}
